'use client'

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import Image from 'next/image'
import { Avatar, cn, Spinner } from '@nextui-org/react'

import ICONS from '@/assets/icons'
import { useAppSelector } from '@/hooks'
import EmojiPost from '@/app/(global)/feed/components/Emoji'
import { MetadataLink } from '@/utils/fetchMetadata'
import AddComment from '@/app/(global)/feed/[...postId]/components/AddComment'
import { feedTypeCard } from '@/redux/api/feed/feed.type'
import { useGetMetaData } from '@/hooks/useGetMetaData'

import styles from './styles.module.css'
import { RenderImagesPost } from './RenderFormFeed/RenderImagesPost'
import { RenderMetaFromLink } from './RenderFormFeed/RenderMetaFromLink'

export const urlRegex = /(https?:\/\/[^\s]+)/g

export const PostContext = createContext<any | null>(null)

export const TextareaPostProvider = ({ children, post }: { children: React.ReactNode; post?: feedTypeCard }) => {
  const [textPost, setTextPost] = useState<string>('')
  const [isCompleted, setIscompleted] = useState<boolean>(false)
  const [images, setImages] = useState<any[]>([])
  const [metadata, setMetadata] = useState<MetadataLink | null>(null)
  const { handleGetLink, metadata: _meta } = useGetMetaData()

  useEffect(() => {
    if (!post) return

    const initDataPost = async () => {
      handleGetLink(post.content)
      setTextPost(post.content)
      setImages(post.imageUrls ?? [])
    }

    initDataPost()
  }, [post])

  useEffect(() => {
    setMetadata(_meta)
  }, [_meta])

  const item = {
    images,
    textPost,
    setImages,
    setMetadata,
    setTextPost,
    isCompleted,
    setIscompleted,
    metadata,
    postId: post?.postId,
  }

  return <PostContext.Provider value={item}>{children}</PostContext.Provider>
}

export const TextareaPost = ({
  type,
  isMobile = false,
  children,
  clearDataImage,
}: {
  children: React.ReactNode
  isMobile?: boolean
  type?: 'comment' | 'reply' | 'post'
  clearDataImage?: any
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const inputFileRef = useRef<HTMLInputElement>(null)
  const user = useAppSelector((state) => state.root.auth.user)
  const { handleGetLink, metadata: _meta, loading: isLoadingMeta } = useGetMetaData()
  const { images, textPost, setImages, setMetadata, metadata, setTextPost } = useContext(PostContext)

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }
  const handleTextChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textInput = e.target.value
    setTextPost(textInput)
    autoResize()
    handleGetLink(textInput)
    // const _meta = await handleGetMeta(textInput)
    // setMetadata(_meta)
  }

  useEffect(() => {
    setMetadata(_meta)
  }, [_meta])

  const placeholder = type === 'comment' ? 'Comment' : type === 'reply' ? 'Reply' : "What's on your mind?"

  const handleOnRemoveImage = useCallback(
    (index: number) => {
      setImages((prev: any) => prev.filter((_: any, i: number) => i !== index))
      if (inputFileRef.current) {
        inputFileRef.current.value = '' // Clear giá trị input file
      }
      if (clearDataImage) {
        clearDataImage()
      }
    },
    [images],
  )

  return (
    <div className={cn('flex w-full gap-2 pt-3', type === 'post' ? 'gap-x-4' : 'gap-x-3')}>
      <div>
        <Avatar
          showFallback
          src={user?.avatarImage || ''}
          fallback={
            <Image
              src="/Sightsea_icon.png"
              alt="Avatar"
              width={type === 'post' ? 40 : 32}
              height={type === 'post' ? 40 : 32}
            />
          }
          className={cn(type === 'post' ? 'h-[40px] w-[40px]' : 'h-[32px] w-[32px]')}
        />
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <div className={type === 'comment' ? 'overflow-auto' : ''}>
          {type === 'comment' ? (
            <AddComment value={textPost} setValue={handleTextChange} />
          ) : (
            <textarea
              ref={textareaRef}
              className={cn(styles.textareaP, 'min-h-20')}
              name="textPost"
              value={textPost}
              onChange={handleTextChange}
              placeholder={placeholder}
            />
          )}
          <RenderImagesPost onRemove={handleOnRemoveImage} readOnly={false} images={images} />

          {!metadata && isLoadingMeta && <Spinner size="sm" />}
          <RenderMetaFromLink onRemove={() => setMetadata(null)} metadata={metadata} />
        </div>

        {!isMobile && (
          <div className="flex w-full items-center justify-between">
            <ul className="flex items-center gap-x-4">
              <li className="cursor-pointer" onClick={() => inputFileRef.current?.click()}>
                <Image
                  src={type === 'post' ? ICONS.addImage : ICONS.addImageWhite}
                  width={20}
                  height={20}
                  alt="Add Image"
                />
                <input
                  multiple={type === 'post' ? true : false}
                  type="file"
                  onChange={(e) => {
                    if (type === 'post') {
                      setImages((prev: any[]) => [...prev, ...(e.target.files as any)])
                    } else {
                      setImages([...(e.target.files as any)])
                    }

                    if (e.target.files?.length) {
                      setMetadata(null)
                    }
                  }}
                  ref={inputFileRef}
                  style={{ display: 'none' }}
                  accept=".png,.svg,.jpg,.jpeg"
                />
              </li>
              <li className="relative cursor-pointer">
                <Image
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsOpen((prev) => !prev)
                  }}
                  src={type === 'post' ? ICONS.emoji : ICONS.emojiWhite}
                  width={20}
                  height={20}
                  alt="Emoji"
                />

                <EmojiPost
                  onSelectIcon={(emoji) => {
                    setTextPost((prev: string) => prev + emoji)
                  }}
                  position={type === 'comment' ? 'top' : 'bottom'}
                  isOpen={isOpen}
                  onToggleOpen={() => setIsOpen(!isOpen)}
                />
              </li>
            </ul>

            {children}
          </div>
        )}
      </div>
    </div>
  )
}
